import { api } from '../api';

import type {
  CardScheduleResponse,
  CreateCardSchedulePayload,
  UpdateCardSchedulePayload,
} from 'api/types/card-schedule.types';

const cardSchedulesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCardSchedule: build.query<CardScheduleResponse | null, number>({
      query: (cardId) => `/cards/${cardId}/schedule.json`,
      providesTags: (_result, _error, cardId) => [
        { type: 'CardSchedule', id: cardId },
      ],
    }),

    getCardScheduleByAnswerId: build.query<CardScheduleResponse | null, string>(
      {
        query: (answerId) => `/card_schedules/by_answer/${answerId}.json`,
        providesTags: (result, _error, answerId) =>
          result
            ? [
                { type: 'CardSchedule', id: result.cardId },
                ...(result.askKlueAnswerId
                  ? [
                      {
                        type: 'CardSchedule' as const,
                        id: result.askKlueAnswerId,
                      },
                    ]
                  : []),
              ]
            : [{ type: 'CardSchedule', id: answerId }],
      },
    ),

    createCardSchedule: build.mutation<
      CardScheduleResponse,
      { cardId: number; payload: CreateCardSchedulePayload }
    >({
      query: ({ cardId, payload }) => ({
        url: `/cards/${cardId}/schedule.json`,
        method: 'POST',
        body: {
          ask_klue_answer_id: payload.askKlueAnswerId,
          prompt: payload.prompt,
          research_mode: payload.researchMode,
          disabled_source_types: payload.disabledSourceTypes,
          frequency_days: payload.frequencyDays,
          last_updated_at: payload.lastUpdatedAt,
        },
      }),
      invalidatesTags: (_result, _error, { cardId, payload }) => [
        { type: 'CardSchedule', id: cardId },
        ...(payload.askKlueAnswerId
          ? [{ type: 'CardSchedule' as const, id: payload.askKlueAnswerId }]
          : []),
      ],
    }),

    updateCardSchedule: build.mutation<
      CardScheduleResponse,
      { cardId: number; payload: UpdateCardSchedulePayload }
    >({
      query: ({ cardId, payload }) => ({
        url: `/cards/${cardId}/schedule.json`,
        method: 'PUT',
        body: {
          prompt: payload.prompt,
          research_mode: payload.researchMode,
          disabled_source_types: payload.disabledSourceTypes,
          frequency_days: payload.frequencyDays,
          last_updated_at: payload.lastUpdatedAt,
        },
      }),
      invalidatesTags: (result, error, { cardId }) => [
        { type: 'CardSchedule', id: cardId },
      ],
    }),

    deleteCardSchedule: build.mutation<void, number>({
      query: (cardId) => ({
        url: `/cards/${cardId}/schedule.json`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, cardId) => [
        { type: 'CardSchedule', id: cardId },
      ],
    }),
  }),
});

export const {
  useGetCardScheduleQuery,
  useLazyGetCardScheduleQuery,
  useGetCardScheduleByAnswerIdQuery,
  useCreateCardScheduleMutation,
  useUpdateCardScheduleMutation,
  useDeleteCardScheduleMutation,
} = cardSchedulesApi;
